import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaComment, FaClock, FaUser, FaPen, FaCheck, FaXmark } from 'react-icons/fa6';
import { updatePedidoCommentByPedidoId } from '../../../../../crud/pedidos_api/pedidos';
import { toast } from 'react-toastify';
import './PedidoCommentsViewer.scss';

interface PedidoCommentsViewerProps {
  show: boolean;
  onHide: () => void;
  pedidos: any[];
  onCommentUpdated?: () => void; // Optional callback to refresh pedidos after update
}

const PedidoCommentsViewer: React.FC<PedidoCommentsViewerProps> = ({
  show,
  onHide,
  pedidos,
  onCommentUpdated,
}) => {
  const [editingPedidoId, setEditingPedidoId] = useState<number | null>(null);
  const [editedComment, setEditedComment] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  // Filter pedidos that have comments
  const pedidosWithComments = pedidos.filter(p => p.comment && p.comment.trim() !== '');

  const handleStartEdit = (pedido: any) => {
    setEditingPedidoId(pedido.pedido_id);
    setEditedComment(pedido.comment || '');
  };

  const handleCancelEdit = () => {
    setEditingPedidoId(null);
    setEditedComment('');
  };

  const handleSaveEdit = async (pedidoId: number) => {
    if (!editedComment.trim()) {
      toast.warning('El comentario no puede estar vacío');
      return;
    }

    setIsSaving(true);
    try {
      await updatePedidoCommentByPedidoId(pedidoId, editedComment);
      toast.success('Comentario actualizado exitosamente');
      
      // Update local pedidos array
      const pedidoToUpdate = pedidos.find(p => p.pedido_id === pedidoId);
      if (pedidoToUpdate) {
        pedidoToUpdate.comment = editedComment;
      }
      
      setEditingPedidoId(null);
      setEditedComment('');
      
      // Call optional callback to refresh data
      if (onCommentUpdated) {
        onCommentUpdated();
      }
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Error al actualizar el comentario');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();
      
      const timeStr = date.toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      
      if (isToday) {
        return `Hoy ${timeStr}`;
      }
      
      const dateStr = date.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: 'short' 
      });
      
      return `${dateStr} ${timeStr}`;
    } catch {
      return dateString;
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Comentarios de Pedidos
          </Modal.Title>
        </div>
        <button className="modal-close-button" onClick={onHide}>
          ×
        </button>
      </Modal.Header>
      <Modal.Body className="comments-viewer-body">
        {pedidosWithComments.length === 0 ? (
          <div className="no-comments-state">
            <FaComment size={48} className="no-comments-icon" />
            <h4>Sin comentarios</h4>
            <p>No hay comentarios registrados para los pedidos de esta mesa</p>
          </div>
        ) : (
          <div className="comments-list">
            {pedidosWithComments.map((pedido) => {
              const isEditing = editingPedidoId === pedido.pedido_id;
              
              return (
                <div key={pedido.pedido_id} className="comment-card">
                  <div className="comment-header">
                    <div className="comment-header-left">
                      <span className="pedido-badge">
                        Pedido #{pedido.pedido_id}
                      </span>
                      {pedido.user_name && (
                        <span className="comment-user">
                          <FaUser size={12} /> {pedido.user_name}
                        </span>
                      )}
                    </div>
                    <div className="comment-header-right">
                      {pedido.created_at && (
                        <span className="comment-time">
                          <FaClock size={12} /> {formatDateTime(pedido.created_at)}
                        </span>
                      )}
                      {!isEditing && (
                        <button 
                          className="edit-comment-btn"
                          onClick={() => handleStartEdit(pedido)}
                          title="Editar comentario"
                        >
                          <FaPen size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="comment-body">
                    <FaComment className="comment-icon" />
                    {isEditing ? (
                      <div className="comment-edit-container">
                        <textarea
                          className="comment-edit-input"
                          value={editedComment}
                          onChange={(e) => setEditedComment(e.target.value)}
                          placeholder="Escribe un comentario..."
                          rows={3}
                          autoFocus
                          disabled={isSaving}
                        />
                        <div className="comment-edit-actions">
                          <Button
                            variant="none"
                            className="comment-save-btn"
                            onClick={() => handleSaveEdit(pedido.pedido_id)}
                            disabled={isSaving || !editedComment.trim()}
                          >
                            <FaCheck /> {isSaving ? 'Guardando...' : 'Guardar'}
                          </Button>
                          <Button
                            variant="none"
                            className="comment-cancel-btn"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                          >
                            <FaXmark /> Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="comment-text">{pedido.comment}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PedidoCommentsViewer;
